import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { api } from '@/lib/api';

const COUNTRIES = [
  { code: '', label: 'All Countries' },
  { code: 'DE', label: 'Germany' },
  { code: 'AT', label: 'Austria' },
  { code: 'CH', label: 'Switzerland' },
  { code: 'PL', label: 'Poland' },
];

const PROPERTY_TYPES = [
  { code: '', label: 'All Types' },
  { code: 'warehouse', label: 'Warehouse' },
  { code: 'logistics', label: 'Logistics' },
  { code: 'production', label: 'Production' },
  { code: 'mixed', label: 'Mixed Use' },
  { code: 'brownfield', label: 'Brownfield' },
  { code: 'land', label: 'Land' },
];

export function PropertyList() {
  const [filters, setFilters] = useState({
    country: '',
    property_type: '',
    min_size: '',
    max_size: '',
    min_price: '',
    max_price: '',
  });
  const [page, setPage] = useState(0);
  const limit = 20;

  const queryParams: Record<string, string> = {
    limit: String(limit),
    offset: String(page * limit),
  };
  Object.entries(filters).forEach(([key, value]) => {
    if (value) queryParams[key] = value;
  });

  const { data, isLoading, error } = useQuery({
    queryKey: ['properties', queryParams],
    queryFn: () => api.properties.list(queryParams),
  });

  const handleFilterChange = (key: string, value: string) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
    setPage(0);
  };

  const totalPages = data ? Math.ceil(data.pagination.total / limit) : 0;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Properties</h1>
        <p className="mt-1 text-gray-600">
          Browse logistics real estate across DACH & Poland
        </p>
      </div>

      {/* Filters */}
      <div className="card p-4">
        <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-6 gap-4">
          <div>
            <label className="label">Country</label>
            <select
              className="input"
              value={filters.country}
              onChange={(e) => handleFilterChange('country', e.target.value)}
            >
              {COUNTRIES.map((c) => (
                <option key={c.code} value={c.code}>
                  {c.label}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="label">Property Type</label>
            <select
              className="input"
              value={filters.property_type}
              onChange={(e) => handleFilterChange('property_type', e.target.value)}
            >
              {PROPERTY_TYPES.map((t) => (
                <option key={t.code} value={t.code}>
                  {t.label}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="label">Min Size (m²)</label>
            <input
              type="number"
              className="input"
              placeholder="e.g., 5000"
              value={filters.min_size}
              onChange={(e) => handleFilterChange('min_size', e.target.value)}
            />
          </div>
          <div>
            <label className="label">Max Size (m²)</label>
            <input
              type="number"
              className="input"
              placeholder="e.g., 50000"
              value={filters.max_size}
              onChange={(e) => handleFilterChange('max_size', e.target.value)}
            />
          </div>
          <div>
            <label className="label">Min Rent (EUR/m²)</label>
            <input
              type="number"
              step="0.1"
              className="input"
              placeholder="e.g., 4.0"
              value={filters.min_price}
              onChange={(e) => handleFilterChange('min_price', e.target.value)}
            />
          </div>
          <div>
            <label className="label">Max Rent (EUR/m²)</label>
            <input
              type="number"
              step="0.1"
              className="input"
              placeholder="e.g., 8.0"
              value={filters.max_price}
              onChange={(e) => handleFilterChange('max_price', e.target.value)}
            />
          </div>
        </div>
      </div>

      {/* Results */}
      {isLoading ? (
        <div className="text-center py-12 text-gray-500">Loading properties...</div>
      ) : error ? (
        <div className="text-center py-12 text-red-500">
          Error loading properties: {(error as Error).message}
        </div>
      ) : !data?.data?.length ? (
        <div className="text-center py-12 text-gray-500">
          No properties found matching your criteria
        </div>
      ) : (
        <>
          <div className="text-sm text-gray-600">
            Showing {data.pagination.offset + 1}-
            {Math.min(data.pagination.offset + limit, data.pagination.total)} of{' '}
            {data.pagination.total.toLocaleString()} properties
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {data.data.map((property) => (
              <Link
                key={property.id}
                to={`/properties/${property.id}`}
                className="card p-4 hover:shadow-md transition-shadow"
              >
                <div className="flex justify-between items-start">
                  <div className="flex-1 min-w-0">
                    <h3 className="font-medium text-gray-900 truncate">{property.title}</h3>
                    <p className="text-sm text-gray-500 mt-1">
                      {property.city}, {property.region && `${property.region}, `}
                      {property.country}
                    </p>
                  </div>
                  <span
                    className={`ml-2 px-2 py-1 text-xs font-medium rounded-full ${
                      property.availability_status === 'available'
                        ? 'bg-green-100 text-green-800'
                        : 'bg-gray-100 text-gray-800'
                    }`}
                  >
                    {property.availability_status || 'Unknown'}
                  </span>
                </div>

                <div className="mt-4 grid grid-cols-2 md:grid-cols-4 gap-2 text-sm">
                  <div>
                    <span className="text-gray-500">Size</span>
                    <p className="font-medium">
                      {property.total_area_sqm?.toLocaleString() || '-'} m²
                    </p>
                  </div>
                  <div>
                    <span className="text-gray-500">Height</span>
                    <p className="font-medium">{property.clear_height_m || '-'} m</p>
                  </div>
                  <div>
                    <span className="text-gray-500">Docks</span>
                    <p className="font-medium">{property.num_docks || '-'}</p>
                  </div>
                  <div>
                    <span className="text-gray-500">Rent</span>
                    <p className="font-medium text-primary-600">
                      {property.rent_eur_sqm
                        ? `${property.rent_eur_sqm.toFixed(2)} EUR/m²`
                        : '-'}
                    </p>
                  </div>
                </div>

                {property.broker_company && (
                  <p className="mt-3 text-xs text-gray-400">via {property.broker_company}</p>
                )}
              </Link>
            ))}
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex justify-center items-center space-x-2">
              <button
                onClick={() => setPage((p) => Math.max(0, p - 1))}
                disabled={page === 0}
                className="btn btn-secondary disabled:opacity-50"
              >
                Previous
              </button>
              <span className="text-sm text-gray-600">
                Page {page + 1} of {totalPages}
              </span>
              <button
                onClick={() => setPage((p) => Math.min(totalPages - 1, p + 1))}
                disabled={page >= totalPages - 1}
                className="btn btn-secondary disabled:opacity-50"
              >
                Next
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
}
